import { useEffect, useState } from 'react';
import { AppIcon } from './AppIcon.js';
import { CoreconfMark } from '../brand/CoreconfMark.js';
import { UpdateBadge } from '../update/UpdateBadge.js';

/**
 * The window bar: where you are, update state, and the window controls.
 *
 * It renders in both builds, which is a change from when it was desktop-only.
 * The reason is what is on it: update state works perfectly well in a tab.
 * What stays desktop-only is
 * minimise/maximise/close — a close button that cannot close is worse than no
 * close button, so the browser simply does not get them.
 *
 * Detection is a marker in the user agent, set by either desktop shell. The
 * Electron candidate also injects a narrow, context-isolated bridge; Tauri
 * keeps using its scoped window capability.
 *
 * ## What the page is allowed to do
 *
 * Window chrome can drag, minimise, maximise and close. Electron additionally
 * exposes its source picker through a separate bridge module loaded only when
 * Share is opened. Neither shell exposes filesystem, shell or process access.
 */

export const isDesktopApp =
  typeof navigator !== 'undefined' && navigator.userAgent.includes('AksolotDesktop');

/** Calls the active shell without importing either desktop SDK into the web build. */
async function windowCommand(cmd: string): Promise<void> {
  const electron = window.aksolotDesktop;
  if (electron?.platform === 'electron') {
    try {
      await electron.windowCommand(cmd as 'minimize' | 'toggle_maximize' | 'close');
    } catch {
      // The bridge validates both the command and the requesting origin.
    }
    return;
  }
  const internals = (globalThis as { __TAURI_INTERNALS__?: { invoke?: unknown } })
    .__TAURI_INTERNALS__;
  const invoke = internals?.invoke as
    | ((command: string, payload?: unknown) => Promise<unknown>)
    | undefined;
  if (invoke === undefined) return;
  try {
    await invoke('plugin:window|' + cmd, { label: 'main' });
  } catch {
    // A refused window command is a capability we did not grant. Nothing to
    // report to the person — the button simply is not there for them.
  }
}

export function TitleBar({ title }: { title: string }): React.ReactElement {
  const [maximized, setMaximized] = useState(false);

  useEffect(() => {
    // Double-click to maximise is a habit older than any of these apps, and
    // losing it makes a custom bar feel broken in a way people cannot name.
    const onResize = () => setMaximized(window.outerWidth >= screen.availWidth - 8);
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return (
    <div
      className="topbar"
      data-tauri-drag-region
      onDoubleClick={() => {
        if (isDesktopApp) void windowCommand('toggle_maximize');
      }}
    >
      {/* The shell does not route through browser history. Keeping arrows here
          made Forward an enabled no-op and Back leave the product entirely;
          the empty column is intentional and keeps the title centred. */}
      <div aria-hidden="true" />

      <div className="topbar-title" data-tauri-drag-region>
        <CoreconfMark size={16} title="" />
        <span data-tauri-drag-region>{title}</span>
      </div>

      <div className="topbar-right">
        <UpdateBadge />
        {/* Inbox and help used to be drawn here with no action behind either.
            A labelled button that does nothing is not a placeholder; it is a
            broken promise, especially to someone navigating by accessible
            name. They return when there is a real destination to open. */}

        {isDesktopApp ? (
          <>
            <button
              className="topbar-btn topbar-win"
              aria-label="Küçült"
              onClick={() => void windowCommand('minimize')}
            >
              <AppIcon name="minus" size={14} hole="transparent" />
            </button>
            <button
              className="topbar-btn topbar-win"
              aria-label={maximized ? 'Önceki boyut' : 'Büyüt'}
              onClick={() => void windowCommand('toggle_maximize')}
            >
              <AppIcon name="square" size={11} hole="transparent" />
            </button>
            <button
              className="topbar-btn topbar-win"
              aria-label="Kapat"
              onClick={() => void windowCommand('close')}
            >
              <AppIcon name="close" size={14} hole="transparent" />
            </button>
          </>
        ) : null}
      </div>
    </div>
  );
}
